import type { ChatCompletion } from "./llm.api";
import type { FetchThreadOptions } from "@/pages/agents/core/context";
import type { Agent, AgentEvent } from "@/schemas/agent.schema";
import type {
  ChannelMessage,
  PulseMessage,
  Thread,
  ThreadHumanControl,
  ThreadWithUsageRequest,
} from "@/schemas/thread.schema";
import type { RequestConfig } from "@/services/api";
import { omit } from "lodash-es";
import { threadTriggers } from "@/schemas/thread.schema";
import { api, handleStream } from "@/services/api";

export interface ThreadAgentOverrides {
  llm_model?: string;
  parameters?: Record<string, unknown>;
}

/** Persisted values echoed by `PATCH /threads/{id}/agent-overrides`. */
export interface ThreadAgentOverridesResult {
  llm_model: string;
  parameters: Record<string, unknown>;
}

export const threadsApi = {
  async create(body: {
    agent_id: string;
    additional_instructions?: string;
    vars?: Record<string, string>;
    agent_overrides?: ThreadAgentOverrides;
  }) {
    const { data } = await api.post<Thread>("/threads", body, {
      headers: {
        "x-triggered-by": "ui",
      },
    });

    return data;
  },

  async getByAgentId(
    agent_id: string,
    options?: FetchThreadOptions,
    config?: RequestConfig
  ): Promise<Thread[]> {
    const baseWhere = {
      agent_id: agent_id,
    } as Record<string, any>;

    if (Array.isArray(options?.trigger)) {
      baseWhere["triggered"] = { inq: options.trigger };
    } else if (threadTriggers.includes(options?.trigger as any)) {
      baseWhere["triggered"] = options?.trigger;
    }

    const where = options?.search
      ? {
          and: [
            baseWhere,
            {
              or: [
                { title: { like: `%${options?.search}%` } },
                { identifier: { like: `%${options?.search}%` } },
              ],
            },
          ],
        }
      : baseWhere;

    const { data } = await api.get<Thread[]>("/threads", {
      ...config,
      params: {
        filter: {
          where,
          order: ["updated_at DESC"],
          limit: options?.limit || 10,
          skip: options?.offset || 0,
        },
      },
      headers: {
        ...config?.headers,
      },
    });

    return data;
  },

  async getById(thread_id: string, config?: RequestConfig): Promise<ThreadWithUsageRequest> {
    const { data } = await api.get<Thread>(`/threads/${thread_id}`, {
      ...config,
      params: {
        include_usage_request: true,
      },
      headers: {
        ...config?.headers,
      },
    });

    return data;
  },

  async updateById(
    thread_id: string,
    body: Partial<Omit<Thread, "id" | "created_at" | "updated_at" | "history">>
  ) {
    await api.patch<void>(`/threads/${thread_id}`, body);
  },

  async run(thread_id: string, message: PulseMessage) {
    const { data } = await api.post(`/threads/${thread_id}/run`, message);

    return data as ChatCompletion;
  },

  async stream(thread_id: string, message: PulseMessage | PulseMessage[]) {
    const abortController = new AbortController();
    const response = await api.post(`/threads/${thread_id}/stream`, message, {
      responseType: "stream",
      signal: abortController.signal,
      headers: {
        "x-stream-mode": "verbose",
      },
    });

    const data = response.data as ReadableStream<any>;

    const stream = handleStream<AgentEvent>(data);

    return { stream, abortController };
  },

  async clear(thread_id: string) {
    await api.post(`/threads/${thread_id}/clear`);
  },

  async delete(thread_id: string) {
    await api.delete(`/threads/${thread_id}`);
  },

  async close(thread_id: string) {
    await api.post(`/threads/${thread_id}/close`);
  },

  async takeover(thread_id: string, config?: RequestConfig): Promise<ThreadHumanControl> {
    const { data } = await api.post<ThreadHumanControl>(
      `/threads/${thread_id}/human-control/takeover`,
      undefined,
      config,
    );
    return data;
  },

  async sendHumanMessage(
    thread_id: string,
    body: { content: string; idempotency_key: string },
    config?: RequestConfig,
  ): Promise<ChannelMessage> {
    const { data } = await api.post<ChannelMessage>(
      `/threads/${thread_id}/human-control/messages`,
      body,
      config,
    );
    return data;
  },

  async retryHumanMessage(
    thread_id: string,
    message_id: string,
    config?: RequestConfig,
  ): Promise<ChannelMessage> {
    const { data } = await api.post<ChannelMessage>(
      `/threads/${thread_id}/human-control/messages/${message_id}/retry`,
      undefined,
      config,
    );
    return data;
  },

  async releaseHumanControl(
    thread_id: string,
    config?: RequestConfig,
  ): Promise<ThreadHumanControl> {
    const { data } = await api.post<ThreadHumanControl>(
      `/threads/${thread_id}/human-control/release`,
      undefined,
      config,
    );
    return data;
  },

  async updateAgentSnapshot(thread_id: string, agent: Agent) {
    await api.patch(
      `/threads/${thread_id}/agent-snapshot`,
      omit(agent, [
        "id",
        "organization_id",
        "created_at",
        "updated_at",
        "agent_deploy_sip",
        "agent_deploy_whatsapp",
        "agent_deploy_chat_widget",
        "post_analysis",
      ])
    );
  },

  // PROTO-1: per-thread model/reasoning written directly into the snapshot.
  // `parameters` replaces `agent_snapshot.parameters` wholesale, so callers
  // must send the full reconciled object. Returns the persisted values.
  async patchAgentOverrides(
    thread_id: string,
    body: ThreadAgentOverrides,
  ): Promise<ThreadAgentOverridesResult> {
    const { data } = await api.patch<ThreadAgentOverridesResult>(
      `/threads/${thread_id}/agent-overrides`,
      body,
    );

    return data;
  },

  async share(thread_id: string) {
    const { data } = await api.post<{
      is_shared: boolean;
      share_token: string;
      share_url: string;
    }>(`/threads/${thread_id}/share`);

    return data;
  },

  async unshare(thread_id: string) {
    const { data } = await api.post<{
      is_shared: boolean;
    }>(`/threads/${thread_id}/unshare`);

    return data;
  },

  async getSharedThread(token: string) {
    const { data } = await api.get<{
      id: string;
      title: string;
      history: PulseMessage[];
      agent: {
        id: string;
        name: string;
        description: string;
      };
      created_at: string;
      shared_at: string;
    }>(`/shared/thread/${token}`);

    return data;
  },

  async getSharedThreadRaw(token: string) {
    const { data } = await api.get<Thread>(`/shared/thread/${token}/raw`);

    return data;
  },
};
